import {
  useState,
  useRef,
  FocusEvent,
  ChangeEvent,
  forwardRef,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
  RefObject,
} from 'react';
import DateTime, { registerLocale } from 'react-datepicker';
import cn from 'classnames';
// import spacetime from 'spacetime';
import ru from 'date-fns/locale/ru';
import { isAfter, isBefore } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { IconTime } from 'UIPackage/IconsModule/components/IconTime';
import { IconCalendar } from 'UIPackage/IconsModule/components/IconCalendar';
import { TIME_FORMAT, DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { compareDates, formatDate, getFormatedClientTime, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';

import styles from './DatePicker.module.scss';

registerLocale('ru', ru);

const convertDatePickerValueToCurrentValue = (value: IDatePickerProps['value'], valueFormat: string) => {
  if (value) {
    const formatedValue = formatDate({ date: value, dateFormat: valueFormat, parseFormat: valueFormat });

    if (formatedValue) {
      return formatedValue;
    }
  }

  return value?.toString();
};

const getLocalValue = (value: string | Date | undefined, valueFormat: string) => {
  if (value) {
    const formatedValue = formatDate({ date: value, dateFormat: valueFormat, parseFormat: valueFormat });

    if (formatedValue) {
      return formatedValue;
    }
  }

  return value?.toString();
};

interface IDatetimeRef extends DateTime {
  state: { open: string };
  setOpen: (state: boolean) => void;
}

export interface IDatePickerProps {
  name: string;
  className?: string;
  classNameError?: string;
  id?: string;
  value?: string | Date;
  placeholder?: ReactNode;
  dateFormat?: string;
  timeFormat?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  opensOnTop?: boolean;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  excludeDates?: Date[];
  showCalendarYear?: boolean;
  postfix?: string;
  onChange?: (date: string | Date) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onCalendarOpen?: () => string | Date | undefined;
}

interface ICustomInputProps {
  name: string;
  className?: string;
  id?: string;
  value?: string;
  inputPlaceholder: ReactNode;
  disable?: boolean;
  isOpenedCalendar: boolean;
  handleChange: (value: string) => void;
  handleInputBlur: (e: FocusEvent<HTMLInputElement>) => void;
  handleButtonTogglerClick: () => void;
}

interface ICalendarContainerProps {
  className?: string;
  timeFormat: boolean;
  children: ReactNode[];
  setTimeView: (value: boolean) => void;
}

interface ICustomTimeInputProps {
  setTimeView: (value: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
  value?: string;
}

const CustomInput = forwardRef(
  (
    {
      id,
      className,
      name,
      inputPlaceholder,
      value,
      disable,
      isOpenedCalendar,
      handleInputBlur,
      handleButtonTogglerClick,
      handleChange,
    }: ICustomInputProps,
    ref: RefObject<HTMLDivElement>,
  ) => {
    const onChange = (e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value);

    return (
      <div ref={ref} className={isOpenedCalendar ? 'react-datepicker-ignore-onclickoutside' : ''}>
        <input
          id={id}
          name={name}
          className={className}
          disabled={disable}
          onBlur={disable ? undefined : handleInputBlur}
          value={value}
          onChange={disable ? undefined : onChange}
        />
        {!value && inputPlaceholder && (
          <div className={styles.Placeholder}>
            <div>{inputPlaceholder}</div>
          </div>
        )}
        <button className={styles.ButtonToggler} type="button" onClick={handleButtonTogglerClick}>
          <IconCalendar className={styles.ButtonTogglerIcon} />
        </button>
      </div>
    );
  },
);

const CalendarContainer = ({ className, children, timeFormat, setTimeView }: ICalendarContainerProps) => {
  function onIconTimeClick() {
    setTimeView(true);
  }

  return (
    <div className={className}>
      {children}
      {timeFormat ? (
        <div className={styles.ShowTimeBtn} onClick={onIconTimeClick}>
          <IconTime className={styles.ShowTimeBtnIcon} />
        </div>
      ) : null}
    </div>
  );
};

const CustomTimeInput = ({ onChange, setTimeView, value }: ICustomTimeInputProps) => {
  let validTime = '';

  if (value) {
    validTime = value;
  } else {
    validTime = '00:00';
  }

  const splitTime = validTime.split(':');

  function handleChangeTime(type: string, action: string): string {
    if (type === 'hours') {
      const hours = action === '+' ? +splitTime[0] + 1 : +splitTime[0] - 1;

      return `${hours}:${splitTime[1]}`;
    }
    if (type === 'minutes') {
      const minutes = action === '+' ? +splitTime[1] + 1 : +splitTime[1] - 1;

      return `${splitTime[0]}:${minutes}`;
    }

    return '';
  }

  function onIconTimeClick() {
    setTimeView(false);
  }

  function onHourInc() {
    onChange(handleChangeTime('hours', '+'));
  }

  function onHourDec() {
    onChange(handleChangeTime('hours', '-'));
  }

  function onMinuteInc() {
    onChange(handleChangeTime('minutes', '+'));
  }

  function onMinuteDec() {
    onChange(handleChangeTime('minutes', '-'));
  }

  return (
    <>
      <div className={styles.BackCalendarBtn} onClick={onIconTimeClick}>
        <IconCalendar className={styles.BackCalendarIcon} />
      </div>
      <div className={styles.TimeWrapper}>
        <div className={styles.TimeContainer}>
          <span className={styles.IncreaseTimeBtn} onClick={onHourInc}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
          <div className={styles.TimeView}>{splitTime[0]}</div>
          <span className={styles.DecreaseTimeBtn} onClick={onHourDec}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
        </div>
        <div className={styles.TimeSeparator}>:</div>
        <div className={styles.TimeContainer}>
          <span className={styles.IncreaseTimeBtn} onClick={onMinuteInc}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
          <div className={styles.TimeView}>{splitTime[1]}</div>
          <span className={styles.DecreaseTimeBtn} onClick={onMinuteDec}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
        </div>
      </div>
    </>
  );
};

export const DatePicker = ({
  className,
  classNameError,
  id,
  name,
  value,
  placeholder = '',
  dateFormat = DATE_FORMAT_LONG_FNS,
  timeFormat = false,
  disabled,
  invalid,
  error,
  opensOnTop,
  minDate = new Date('01-01-1917'),
  maxDate = new Date('12-31-2050'),
  excludeDates,
  showCalendarYear = true,
  onChange,
  onBlur,
  postfix,
  onCalendarOpen,
}: IDatePickerProps) => {
  const valueFormat = timeFormat ? `${dateFormat} ${TIME_FORMAT}` : dateFormat;
  const localValue = useMemo(() => {
    return getLocalValue(value, valueFormat);
  }, [value, valueFormat]);
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    convertDatePickerValueToCurrentValue(value, valueFormat),
  );
  const [lastValidValue, setLastValidValue] = useState<typeof currentValue>('');
  const [isOpenedCalendar, setIsOpenedCalendar] = useState<boolean>(false);
  const [isRenderTimeView, setIsRenderTimeView] = useState<boolean>(false);
  const datetimeRef = useRef<IDatetimeRef>(null);
  const ref = useRef(null);

  registerLocale('ru', ru);

  const convertStringToDate = useCallback(
    (inputValue: string): string | undefined => {
      if (inputValue?.length < (valueFormat.length || 8)) {
        return;
      }
      const currentStringValue = `${inputValue}`;
      const timePart: string | undefined = currentStringValue?.split(' ')[1];
      const isTime = timeFormat && !!timePart;
      const currentValueFormat = timeFormat ? valueFormat.split(' ')[0].split('.') : valueFormat.split('.');
      const parseFormat = timeFormat
        ? (inputValue.split(' ')[1] && valueFormat) || currentValueFormat.join('.')
        : valueFormat;
      let newMomentDate = formatDate({
        date: currentStringValue,
        dateFormat: valueFormat,
        parseFormat,
      });

      if (!newMomentDate) {
        let formatedValue = currentValueFormat.reduce((acc, item, index) => {
          return `${acc.length ? `${acc}.` : ''}${currentStringValue
            ?.replaceAll('.', '')
            .slice(index ? item.length : 0, index ? item.length * 2 : item.length)}`;
        }, '');

        if (isTime) {
          const currentTimeFormat = TIME_FORMAT.split(':');

          formatedValue = `${formatedValue} ${currentTimeFormat.reduce(
            (acc, item, index) =>
              `${acc.length ? `${acc}:` : ''}${timePart
                ?.replaceAll(':', '')
                .slice(index ? item.length : 0, index ? item.length * 2 : item.length)}`,
            '',
          )}`;
        }
        newMomentDate = formatDate({
          date: formatedValue,
          dateFormat: valueFormat,
          parseFormat: !isTime ? currentValueFormat.join('.') : `${currentValueFormat.join('.')} ${TIME_FORMAT}`,
        });
      }

      return newMomentDate;
    },
    [timeFormat, valueFormat],
  );

  useEffect(() => {
    if (!currentValue) {
      setCurrentValue(convertDatePickerValueToCurrentValue(localValue, valueFormat));
    }
  }, [currentValue, localValue, valueFormat]);

  useEffect(() => {
    if (!value && currentValue) {
      setCurrentValue(undefined);
    }
  }, [currentValue, value]);

  useEffect(() => {
    if (currentValue && !lastValidValue) {
      const lastValidValueMoment = convertStringToDate(currentValue);

      if (lastValidValueMoment) {
        const momentMinDate = formatDate({ date: minDate, dateFormat: valueFormat, parseFormat: valueFormat });
        const momentMaxDate = formatDate({ date: maxDate, dateFormat: valueFormat, parseFormat: valueFormat });

        if (
          compareDates({ dateLeft: lastValidValueMoment, dateRight: momentMinDate, parseFormat: valueFormat }) !== -1 &&
          compareDates({ dateLeft: lastValidValueMoment, dateRight: momentMaxDate, parseFormat: valueFormat }) !== 1
        ) {
          setLastValidValue(lastValidValueMoment);
        }
      }
    }
  }, [convertStringToDate, currentValue, lastValidValue, maxDate, minDate, valueFormat]);

  const handleOpen = useCallback(() => {
    if (!currentValue) {
      if (onCalendarOpen) {
        const customDate = onCalendarOpen();

        if (customDate) {
          const formattedDate =
            typeof customDate === 'string'
              ? customDate
              : formatDate({ date: customDate, dateFormat: valueFormat, parseFormat: valueFormat });

          setCurrentValue(formattedDate);

          if (onChange) {
            onChange(formattedDate);
          }

          return;
        }
      }

      let currentDate = '';

      const minDateString = formatDate({
        date: minDate,
        dateFormat: valueFormat,
        parseFormat: valueFormat,
      });

      const maxDateString = formatDate({
        date: maxDate,
        dateFormat: valueFormat,
        parseFormat: valueFormat,
      });

      if (
        minDate &&
        minDateString !== '01.01.1917' &&
        minDateString !== '01.01.1917 00:00' &&
        isAfter(minDate, new Date())
      ) {
        currentDate = minDateString;
      } else if (maxDate && isBefore(maxDate, new Date())) {
        currentDate = maxDateString;
      } else {
        currentDate = formatDate({
          date: getFormatedClientTime(valueFormat),
          dateFormat: valueFormat,
          parseFormat: valueFormat,
        });
      }

      setCurrentValue(currentDate);

      if (onChange) {
        onChange(currentDate);
      }
    }
  }, [currentValue, onChange, valueFormat, minDate, maxDate, onCalendarOpen]);

  const handleChange = useCallback(
    (date: string | Date) => {
      let newCurrentValue: string = date as string;

      if (date instanceof Date) {
        newCurrentValue = formatDate({ date, dateFormat: valueFormat, parseFormat: valueFormat });
      }
      // const validDate = spacetime(newCurrentValue);

      if (postfix && typeof date === 'string') {
        newCurrentValue = date.replace(` ${postfix}`, '');
      }

      setCurrentValue(newCurrentValue);

      if (onChange) {
        onChange(newCurrentValue);
      }
      // if (validDate.isValid()) {
      //   if (onChange) {
      //     onChange(newCurrentValue);
      //   }
      // }
    },
    [onChange, valueFormat],
  );

  const compareLastValidValue = useCallback(
    ({ minDateValue, maxDateValue }: { minDateValue?: Date; maxDateValue?: Date }) => {
      const lastValidValueMoment = lastValidValue ? convertStringToDate(lastValidValue) : undefined;
      const criticalDate = formatDate({
        date: minDateValue || maxDateValue || '',
        dateFormat: valueFormat,
        parseFormat: valueFormat,
      });

      if (
        lastValidValueMoment &&
        compareDates({ dateLeft: lastValidValueMoment, dateRight: criticalDate, parseFormat: valueFormat }) ===
          (minDateValue ? 1 : -1)
      ) {
        handleChange(criticalDate);
      } else if (lastValidValueMoment) {
        handleChange(lastValidValueMoment);
      } else {
        handleChange(criticalDate);
      }
    },
    [convertStringToDate, handleChange, lastValidValue, valueFormat],
  );

  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { value: inputValue } = e.currentTarget;
      const lastValidValueMoment = lastValidValue ? convertStringToDate(lastValidValue) : undefined;

      if (inputValue) {
        const newMomentDate = convertStringToDate(inputValue);

        if (newMomentDate) {
          const momentMinDate = formatDate({ date: minDate, dateFormat: valueFormat, parseFormat: valueFormat });

          if (timeFormat) {
            maxDate.setHours(23);
            maxDate.setMinutes(59);
          }

          const momentMaxDate = formatDate({ date: maxDate, dateFormat: valueFormat, parseFormat: valueFormat });

          if (compareDates({ dateLeft: newMomentDate, dateRight: momentMinDate, parseFormat: valueFormat }) === -1) {
            compareLastValidValue({ minDateValue: minDate });
          } else if (
            compareDates({ dateLeft: newMomentDate, dateRight: momentMaxDate, parseFormat: valueFormat }) === 1
          ) {
            compareLastValidValue({ maxDateValue: maxDate });
          } else {
            handleChange(newMomentDate);
            setCurrentValue(newMomentDate);
            setLastValidValue(newMomentDate);
          }
        } else {
          handleChange(lastValidValueMoment || '');
        }
      } else if (lastValidValueMoment) {
        setLastValidValue(lastValidValueMoment);
      }
      if (onBlur) {
        onBlur(e);
      }
    },
    [
      lastValidValue,
      convertStringToDate,
      onBlur,
      minDate,
      valueFormat,
      maxDate,
      compareLastValidValue,
      handleChange,
      timeFormat,
    ],
  );

  const handleButtonTogglerClick = useCallback(() => {
    if (!disabled && datetimeRef.current) {
      setIsOpenedCalendar(!isOpenedCalendar);

      if (!datetimeRef.current.state.open) {
        handleOpen();
        datetimeRef.current.setOpen(true);
      } else {
        datetimeRef.current.setOpen(false);
      }
    }
  }, [disabled, handleOpen, isOpenedCalendar]);

  const handleCloseCalendar = useCallback(() => {
    setIsOpenedCalendar(false);
    setIsRenderTimeView(false);
  }, []);

  const currentMomentValue = formatDate({
    date: currentValue || '',
    dateFormat: valueFormat,
    parseFormat: valueFormat,
  });
  const validatedValue = currentMomentValue
    ? parseDate({ date: currentMomentValue, parseFormat: valueFormat })
    : undefined;

  const renderCalendarContainer = useCallback(
    (props: { children: ReactNode[] }) => (
      <CalendarContainer timeFormat={timeFormat} setTimeView={setIsRenderTimeView} {...props} />
    ),
    [timeFormat],
  );

  const onDateChange = useCallback((date: Date) => handleChange(date), [handleChange]);

  useEffect(() => {
    handleChange(value === undefined ? '' : value);
  }, [value]);

  return (
    <div
      className={cn(
        styles.DatePicker,
        {
          [styles.DatePickerDisabled]: disabled,
          [styles.DatePickerInvalid]: invalid,
          [styles.DatePickerOpensOnTop]: opensOnTop,
        },
        className,
      )}
      ref={ref}
    >
      <DateTime
        ref={datetimeRef}
        className={cn(styles.Input, {
          [styles.InputDisabled]: disabled,
          [styles.InputInvalid]: invalid,
        })}
        selected={validatedValue}
        onChange={onDateChange}
        onCalendarClose={handleCloseCalendar}
        timeFormat={TIME_FORMAT}
        locale="ru"
        value={postfix && currentValue ? `${currentValue} ${postfix}` : currentValue}
        showTimeInput={isRenderTimeView}
        customTimeInput={<CustomTimeInput setTimeView={setIsRenderTimeView} />}
        customInput={
          <CustomInput
            id={id}
            name={name}
            inputPlaceholder={placeholder}
            handleChange={handleChange}
            handleInputBlur={handleInputBlur}
            handleButtonTogglerClick={handleButtonTogglerClick}
            disable={disabled}
            isOpenedCalendar={isOpenedCalendar}
          />
        }
        minDate={minDate}
        maxDate={maxDate}
        excludeDates={excludeDates}
        calendarContainer={renderCalendarContainer}
        dateFormatCalendar={!showCalendarYear ? 'LLLL' : undefined}
      />
      {invalid && error && <div className={cn(styles.DatePickerError, classNameError)}>{error}</div>}
      {!invalid && !error && <ErknmFillInProcessMessage name={name} />}
    </div>
  );
};
